import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  ActivityFeedTable,
  AnalyticsOverview,
  DailyVolumeChart,
  DailyCostChart,
  ActionDistributionChart,
  QueueStatusWidget,
  TimeRangeFilter,
  TokenStatusBadge,
  TokenActions,
  FollowUpTab,
  MarkLostTab,
} from '@/components/ghl-dashboard'
import {
  useGHLActivityFeed,
  useGHLDashboardStats,
  useGHLDailyStats,
  useGHLLocationNames,
  useGHLQueueStatus,
  useGHLFollowUpFeed,
  useGHLFollowUpStats,
  useGHLAwaitingMarkLost,
  useGHLMarkedLostHistory,
} from '@/hooks/useGHLDashboard'
import { useUrlTimeRange } from '@/hooks/useUrlTimeRange'
import { ArrowLeft, RefreshCw, Settings } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useTokenHealthMap } from '@/hooks/useTokenHealth'
import type { TokenStatus } from '@/hooks/useTokenHealth'
import { LocationSettingsDialog } from '@/components/admin/LocationSettingsDialog'

const MIDDLEWARE_URL = import.meta.env.VITE_GHL_MIDDLEWARE_URL || null

/**
 * Valid tab segments for the dashboard URL. Keep in sync with the TabsTrigger
 * `value` props below. Invalid values in the URL get rewritten to the default.
 */
const VALID_TABS = ['feed', 'analytics', 'followups', 'lost'] as const
type DashboardTab = (typeof VALID_TABS)[number]
const DEFAULT_TAB: DashboardTab = 'feed'

function isValidTab(value: string | undefined): value is DashboardTab {
  return typeof value === 'string' && (VALID_TABS as readonly string[]).includes(value)
}

/**
 * Recommended-action values available as feed filters. Keep values aligned
 * with what the middleware writes to `ghl_conversation_analyses.recommended_action`
 * — mismatch here silently returns an empty feed because the server-side
 * predicate is an exact string match.
 */
const FEED_ACTION_FILTERS = [
  { value: 'all', label: 'All actions' },
  { value: 'MOVE_TO_BOOKED', label: 'Booked' },
  { value: 'UPDATE_FIELDS_ONLY', label: 'Fields Updated' },
  { value: 'MARK_LOST', label: 'Marked Lost' },
  { value: 'NO_ACTION', label: 'No Action' },
] as const

export default function GHLDashboardPage() {
  const { locationId, tab } = useParams<{ locationId: string; tab: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // Resolve the URL's tab segment to a validated tab value. Unknown values
  // fall back to the default and we rewrite the URL in an effect below so the
  // address bar matches what's actually rendered.
  const activeTab: DashboardTab = isValidTab(tab) ? tab : DEFAULT_TAB

  useEffect(() => {
    if (!locationId) return
    if (tab && !isValidTab(tab)) {
      // URL contains a bogus tab segment (e.g., /location/XYZ/banana). Rewrite
      // to the default tab without polluting history. Preserve search so the
      // user's time-range filter survives the rewrite.
      navigate(
        {
          pathname: `/admin/ghl-middleware/location/${locationId}/${DEFAULT_TAB}`,
          search: location.search,
        },
        { replace: true }
      )
    }
  }, [tab, locationId, navigate])

  const setActiveTab = (newTab: string) => {
    if (!locationId || !isValidTab(newTab)) return
    // Preserve search params (time range, page, action filter) when switching
    // tabs so the user's filter context survives navigation.
    navigate(
      {
        pathname: `/admin/ghl-middleware/location/${locationId}/${newTab}`,
        search: location.search,
      },
      { replace: true }
    )
  }

  // Time range is URL-backed via ?from&until&range_label — survives tab
  // switches (handled by setActiveTab's search preservation), reloads, and
  // shared links.
  const [timeRange, setTimeRange] = useUrlTimeRange()
  const [followUpPage, setFollowUpPage] = useState(0)
  const [followUpStatusFilter, setFollowUpStatusFilter] = useState('all')
  const [lostHistoryPage, setLostHistoryPage] = useState(0)

  // ─── Feed state (URL-backed) ────────────────────────────────────────
  // Keep feed pagination + filter in the URL query string so refresh / back
  // button / copy-link all preserve the exact view. Page is 1-indexed in the
  // URL for UX clarity ("page 2" = 2nd page), 0-indexed internally.
  const [searchParams, setSearchParams] = useSearchParams()
  const feedPage = Math.max(0, (parseInt(searchParams.get('page') ?? '1', 10) || 1) - 1)
  const feedActionParam = searchParams.get('action')
  const feedActionFilter = feedActionParam && feedActionParam !== 'all' ? feedActionParam : 'all'

  const updateFeedParams = useCallback(
    (updates: { page?: number; action?: string }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (updates.page !== undefined) {
            // Omit page=1 to keep the URL clean on the first page.
            if (updates.page <= 0) next.delete('page')
            else next.set('page', String(updates.page + 1))
          }
          if (updates.action !== undefined) {
            if (!updates.action || updates.action === 'all') next.delete('action')
            else next.set('action', updates.action)
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setFeedPage = useCallback(
    (p: number) => updateFeedParams({ page: p }),
    [updateFeedParams]
  )

  const setFeedActionFilter = useCallback(
    (action: string) => updateFeedParams({ action, page: 0 }),
    [updateFeedParams]
  )

  // All hooks are now scoped to the selected location
  const feed = useGHLActivityFeed(
    timeRange,
    feedPage,
    25,
    activeTab === 'feed',
    locationId,
    feedActionFilter === 'all' ? null : feedActionFilter
  )

  // Clamp out-of-range page params (e.g., user lands on `?page=5` but the
  // filtered result set only has 1 page). Runs after each feed resolve so
  // the UI never shows "Page 5 of 1". Uses replace: true in setFeedPage so
  // history isn't polluted with the transient bad page.
  useEffect(() => {
    if (feed.isLoading || !feed.data) return
    const totalPages = Math.max(1, Math.ceil(feed.data.count / 25))
    if (feedPage > totalPages - 1) {
      setFeedPage(0)
    }
  }, [feed.isLoading, feed.data, feedPage, setFeedPage])
  const stats = useGHLDashboardStats(timeRange, activeTab === 'analytics', locationId)
  const dailyStats = useGHLDailyStats(timeRange, activeTab === 'analytics', locationId)
  const locationNames = useGHLLocationNames()
  const queue = useGHLQueueStatus(MIDDLEWARE_URL)
  const followUpFeed = useGHLFollowUpFeed(timeRange, followUpPage, followUpStatusFilter, locationId ?? '', 25, activeTab === 'followups')
  const followUpStats = useGHLFollowUpStats(timeRange, activeTab === 'followups', locationId)
  const awaitingMarkLost = useGHLAwaitingMarkLost(locationId, 100, activeTab === 'lost')
  const markedLostHistory = useGHLMarkedLostHistory(timeRange, lostHistoryPage, locationId, 25, activeTab === 'lost')

  const locationName = locationId ? locationNames.data?.get(locationId) : null

  const tokenHealth = useTokenHealthMap(!!locationId)
  const locToken = locationId ? tokenHealth.data?.locations[locationId] : undefined
  const tokenStatus: TokenStatus = locToken?.status ?? 'unknown'

  // ─── Manual refresh ─────────────────────────────────────────────────
  // Refetches the currently active tab's data plus the persistent widgets
  // (queue status, token health). Uses per-query refetch() rather than
  // queryClient.invalidateQueries so inactive tabs stay dormant until the
  // user switches to them.
  //
  // `isManualRefreshing` is a separate state from React Query's own fetching
  // flags. Its purpose: cascade a "show skeleton" signal to every widget on
  // the page *only* when the user explicitly clicked Refresh — so the 15s
  // Feed auto-refresh doesn't flicker the UI every time it ticks.
  const [isManualRefreshing, setIsManualRefreshing] = useState(false)

  const refresh = async () => {
    setIsManualRefreshing(true)
    try {
      const refetches: Promise<unknown>[] = []
      if (activeTab === 'feed') {
        refetches.push(feed.refetch())
      } else if (activeTab === 'analytics') {
        refetches.push(stats.refetch(), dailyStats.refetch())
      } else if (activeTab === 'followups') {
        refetches.push(followUpFeed.refetch(), followUpStats.refetch())
      } else if (activeTab === 'lost') {
        refetches.push(awaitingMarkLost.refetch(), markedLostHistory.refetch())
      }
      refetches.push(queue.refetch(), tokenHealth.refetch())
      // allSettled (not all) so a single failed refetch doesn't leave the
      // skeleton flag stuck on forever.
      await Promise.allSettled(refetches)
    } finally {
      setIsManualRefreshing(false)
    }
  }

  const [settingsOpen, setSettingsOpen] = useState(false)

  // True while ANY of the currently-relevant queries are in flight — drives
  // the spinning icon + disabled state on the button. `isFetching` (not
  // `isLoading`) so the spinner also animates on subsequent manual refreshes.
  const isRefreshing =
    (activeTab === 'feed' && feed.isFetching) ||
    (activeTab === 'analytics' && (stats.isFetching || dailyStats.isFetching)) ||
    (activeTab === 'followups' && (followUpFeed.isFetching || followUpStats.isFetching)) ||
    (activeTab === 'lost' && (awaitingMarkLost.isFetching || markedLostHistory.isFetching)) ||
    queue.isFetching ||
    tokenHealth.isFetching

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => navigate('/admin/ghl-middleware')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">
                {locationName ?? locationId ?? 'Location'}
              </h1>
              <TokenStatusBadge status={tokenStatus} detail={locToken} size="md" />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setSettingsOpen(true)}
                      disabled={!locationId}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    Middleware settings for this location
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-sm text-muted-foreground">GHL Middleware — AI conversation analysis</p>
              <TokenActions
                locationId={locationId ?? ''}
                tokenStatus={tokenStatus}
                middlewareUrl={MIDDLEWARE_URL}
                variant="with-label"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refresh}
                  disabled={isRefreshing}
                  className="gap-2"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                Refetch data for the current tab
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TimeRangeFilter value={timeRange} onChange={(r) => {
            // NB: do not call setFeedPage(0) here — it issues a second
            // setSearchParams in the same tick, which races with
            // setTimeRange's URL update (each updater reads the live URL,
            // not the in-flight one, so the last call wins and the
            // from/until/range_label we just wrote get clobbered).
            // setTimeRange already clears `page` itself for this reason.
            // The Follow-up + Qual + Lost-history pages are component
            // state (not URL-backed), so they're safe to reset here.
            setTimeRange(r)
            setFollowUpPage(0)
            setLostHistoryPage(0)
          }} />
        </div>
      </div>

      {/* Queue Status (persistent) */}
      <QueueStatusWidget
        status={queue.data}
        isLoading={queue.isLoading || isManualRefreshing}
        isError={queue.isError}
      />

      {/* Tabs — 4 tabs, each a URL segment under /location/:locationId/:tab */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="feed">
            Live Feed
            {feed.data && feed.data.count > 0 && (
              <span className="ml-1.5 text-xs text-muted-foreground">({feed.data.count.toLocaleString()})</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="followups">
            Follow-Ups
            {followUpStats.data && followUpStats.data.pending > 0 && (
              <span className="ml-1.5 text-xs text-muted-foreground">({followUpStats.data.pending})</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="lost">
            Mark-Lost
            {awaitingMarkLost.data && awaitingMarkLost.data.length > 0 && (
              <span className="ml-1.5 text-xs text-muted-foreground">({awaitingMarkLost.data.length})</span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Feed Tab */}
        <TabsContent value="feed" className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Select value={feedActionFilter} onValueChange={setFeedActionFilter}>
                <SelectTrigger className="w-[180px] h-8">
                  <SelectValue placeholder="Filter action" />
                </SelectTrigger>
                <SelectContent>
                  {FEED_ACTION_FILTERS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Auto-refreshing every 15 seconds
              </p>
            </div>
            {feed.dataUpdatedAt && (
              <p className="text-xs text-muted-foreground">
                Updated {new Date(feed.dataUpdatedAt).toLocaleTimeString()}
              </p>
            )}
          </div>
          <ActivityFeedTable
            data={feed.data?.data ?? []}
            count={feed.data?.count ?? 0}
            page={feedPage}
            pageSize={25}
            isLoading={feed.isLoading || isManualRefreshing}
            onPageChange={setFeedPage}
            locationNames={locationNames.data}
          />
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <AnalyticsOverview stats={stats.data} isLoading={stats.isLoading || isManualRefreshing} />

          <div className="grid gap-6 lg:grid-cols-2">
            <DailyVolumeChart data={dailyStats.data ?? []} />
            <DailyCostChart data={dailyStats.data ?? []} />
          </div>

          <div className="grid gap-6 lg:grid-cols-1">
            <ActionDistributionChart data={stats.data?.by_action ?? {}} />
          </div>
        </TabsContent>

        {/* Follow-Ups Tab */}
        <TabsContent value="followups" className="space-y-4">
          <FollowUpTab
            data={followUpFeed.data?.data ?? []}
            count={followUpFeed.data?.count ?? 0}
            page={followUpPage}
            pageSize={25}
            isLoading={followUpFeed.isLoading || isManualRefreshing}
            onPageChange={setFollowUpPage}
            stats={followUpStats.data}
            statsLoading={followUpStats.isLoading || isManualRefreshing}
            statusFilter={followUpStatusFilter}
            onStatusFilterChange={(s) => { setFollowUpStatusFilter(s); setFollowUpPage(0) }}
            locationNames={locationNames.data}
          />
        </TabsContent>

        {/* Mark-Lost Tab — opportunities in the per-location grace window
            (awaiting auto-mark-lost) and history of opportunities whose
            grace expired and the lost-status write fired. Pulls from
            ghl_followup_schedule step-99 (MARK_LOST_GRACE_STEP) rows. */}
        <TabsContent value="lost" className="space-y-4">
          <MarkLostTab
            awaitingRows={awaitingMarkLost.data ?? []}
            awaitingLoading={awaitingMarkLost.isLoading || isManualRefreshing}
            historyRows={markedLostHistory.data?.data ?? []}
            historyCount={markedLostHistory.data?.count ?? 0}
            historyPage={lostHistoryPage}
            historyPageSize={25}
            historyLoading={markedLostHistory.isLoading || isManualRefreshing}
            onHistoryPageChange={setLostHistoryPage}
          />
        </TabsContent>
      </Tabs>

      {locationId && (
        <LocationSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          locationId={locationId}
          locationName={locationName ?? null}
        />
      )}
    </div>
  )
}
